import { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import EmptyState from '@/components/admin/EmptyState';
import { adminApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Trash2, Edit3, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '@/components/admin/ImageUpload';
import { ATTRACTION_CATEGORIES, ATTRACTION_CATEGORY_LABELS } from '@/lib/data';

const EMPTY = { title: '', description: '', image_url: '', category: 'acuaticos', visible: true, featured: false, order: 0, cta_label: 'Ven a jugar', cta_target: 'whatsapp', cta_url: '', bullets: [] };

function Form({ value, onChange }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    const bulletsText = (value.bullets || []).join('\n');
    return (
        <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Título</Label><Input value={value.title} onChange={(e) => set('title', e.target.value)} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Descripción</Label><Textarea rows={3} value={value.description} onChange={(e) => set('description', e.target.value)} className="mt-1" /></div>
            <div className="sm:col-span-2"><ImageUpload value={value.image_url} onChange={(v) => set('image_url', v)} label="Imagen" testId="attraction-image" /></div>
            <div><Label>Categoría</Label>
                <Select value={value.category} onValueChange={(v) => set('category', v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                    {ATTRACTION_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.key} value={cat.key}>
                            {cat.label}
                        </SelectItem>
                    ))}
                </SelectContent>    
                </Select>
            </div>
            <div><Label>Orden</Label><Input type="number" value={value.order} onChange={(e) => set('order', Number(e.target.value))} className="mt-1" /></div>
            <div><Label>CTA Label</Label><Input value={value.cta_label} onChange={(e) => set('cta_label', e.target.value)} className="mt-1" /></div>
            <div><Label>CTA Target</Label>
                <Select value={value.cta_target} onValueChange={(v) => set('cta_target', v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="contact">Contacto</SelectItem>
                        <SelectItem value="external">URL externa</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="sm:col-span-2"><Label>URL CTA (si externa)</Label><Input value={value.cta_url} onChange={(e) => set('cta_url', e.target.value)} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Bullets (uno por línea)</Label><Textarea rows={3} value={bulletsText} onChange={(e) => set('bullets', e.target.value.split('\n').filter(Boolean))} className="mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={value.visible} onCheckedChange={(v) => set('visible', v)} /> Visible en el sitio</div>
            <div className="flex items-center gap-2"><Switch checked={value.featured} onCheckedChange={(v) => set('featured', v)} /> Destacada</div>
        </div>
    );
}

export default function AdminAttractionsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const load = () => { setLoading(true); adminApi.attractions.list().then(setItems).finally(() => setLoading(false)); };
    useEffect(() => { load(); }, []);
    const startCreate = () => { setForm(EMPTY); setCreating(true); };
    const startEdit = (it) => { setEditing(it.id); setForm({ ...EMPTY, ...it }); };
    const save = async () => {
        try {
            if (creating) { await adminApi.attractions.create(form); toast.success('Atracción creada'); setCreating(false); }
            else if (editing) { await adminApi.attractions.update(editing, form); toast.success('Atracción actualizada'); setEditing(null); }
            load();
        } catch { toast.error('No se pudo guardar'); }
    };
    const remove = async (id) => { await adminApi.attractions.delete(id); toast.success('Eliminada'); load(); };
    return (
        <AdminLayout title="Atracciones">
            <div className="flex items-center justify-between mb-4"><h2 className="text-sm text-muted-foreground">Administra las atracciones que se muestran en el sitio.</h2>
                <Dialog open={creating} onOpenChange={setCreating}>
                    <DialogTrigger asChild>
                        <button onClick={startCreate} data-testid="admin-attractions-new" className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm"><Plus className="size-4" /> Nueva atracción</button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Nueva atracción</DialogTitle><DialogDescription>Visible al guardar.</DialogDescription></DialogHeader>
                        <Form value={form} onChange={setForm} />
                        <DialogFooter><button onClick={save} data-testid="admin-attractions-save" className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm">Guardar</button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
            {loading ? (
                <div className="rounded-2xl bg-white border border-border h-64 animate-pulse" />
            ) : items.length === 0 ? (
                <EmptyState title="Sin atracciones" description="Crea la primera para mostrarla en /atracciones." />
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {items.map((it) => (
                        <div key={it.id} className="rounded-2xl bg-white border border-border overflow-hidden shadow-[var(--shadow-soft)]">
                            <div className="aspect-[16/10] bg-secondary relative">{it.image_url ? <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-xs text-muted-foreground">Sin imagen</div>}
                                <span className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-white/90 border border-border">{ATTRACTION_CATEGORY_LABELS[it.category] || it.category}</span>
                                <span className={`absolute top-2 right-2 text-[10px] px-2 py-1 rounded-full ${it.visible ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'} inline-flex items-center gap-1`}>{it.visible ? <Eye className="size-3" /> : <EyeOff className="size-3" />} {it.visible ? 'Visible' : 'Oculta'}</span>
                            </div>
                            <div className="p-4">
                                <h3 className="font-display font-semibold">{it.title}</h3>
                                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{it.description}</p>
                                <div className="mt-3 flex items-center gap-2">
                                    <Dialog open={editing === it.id} onOpenChange={(o) => !o && setEditing(null)}>
                                        <DialogTrigger asChild><button onClick={() => startEdit(it)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-secondary" data-testid={`attraction-edit-${it.id}`}><Edit3 className="size-3.5" /> Editar</button></DialogTrigger>
                                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                            <DialogHeader><DialogTitle>Editar atracción</DialogTitle></DialogHeader>
                                            <Form value={form} onChange={setForm} />
                                            <DialogFooter><button onClick={save} className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm">Guardar cambios</button></DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild><button className="inline-flex items-center gap-1 rounded-lg bg-destructive text-destructive-foreground px-2.5 py-1.5 text-xs" data-testid={`attraction-delete-${it.id}`}><Trash2 className="size-3.5" /> Borrar</button></AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader><AlertDialogTitle>¿Eliminar?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                                            <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(it.id)}>Eliminar</AlertDialogAction></AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AdminLayout>
    );
}
